import { z } from "zod";

/**
 * AP exception framework — shared vocabulary.
 *
 * An **exception rule set** is "vocabulary + ownership" in one place: it
 * points at a Core Enum (the exception types an LLM may raise for an invoice)
 * and carries an ordered list of rules mapping an exception type to the owner
 * who must resolve it. The `match_invoice_line_items` workflow node binds one
 * rule set; after matching it evaluates the invoice against the bound enum,
 * persists the open exceptions on the invoice, and hands the wired task to the
 * first rule (by array order) whose type was raised. A rule that names a task
 * type instead opens its own task of that type for each exception it owns.
 */

export const AP_EXCEPTION_OWNER_TYPES = [
  "user",
  "user_group",
  "agent",
  "workflow",
] as const;
export type ApExceptionOwnerType = (typeof AP_EXCEPTION_OWNER_TYPES)[number];

export const AP_EXCEPTION_GROUP_ALLOCATIONS = [
  "round_robin",
  "least_open_tasks",
] as const;
export type ApExceptionGroupAllocation =
  (typeof AP_EXCEPTION_GROUP_ALLOCATIONS)[number];

export const ApExceptionRuleOwnerSchema = z.object({
  type: z.enum(AP_EXCEPTION_OWNER_TYPES),
  /** User, user group, agent definition, or workflow (lineage) id. */
  id: z.uuid(),
  /** How a `user_group` pool picks one member per hand-off. */
  allocation: z.enum(AP_EXCEPTION_GROUP_ALLOCATIONS).optional(),
});
export type ApExceptionRuleOwner = z.infer<typeof ApExceptionRuleOwnerSchema>;

export const ApExceptionRuleSchema = z.object({
  /** Core Enum option of the rule set's enum. */
  exceptionTypeOptionId: z.uuid(),
  /**
   * Extra instruction for the LLM when deciding whether this type applies.
   * Separate from the enum option description, which stays human-facing.
   */
  detectionGuidance: z.string().trim().max(2000).nullable().optional(),
  owner: ApExceptionRuleOwnerSchema,
  /**
   * Task type (anchored to AP invoices) opened per exception of this type and
   * closed when it resolves. Unset = the exception is routed on the single
   * task wired into the match node.
   */
  taskTypeKey: z.string().trim().min(1).max(200).nullable().optional(),
});
export type ApExceptionRule = z.infer<typeof ApExceptionRuleSchema>;

/** Ordered by priority: the first rule whose type is raised owns the invoice. */
export const ApExceptionRulesSchema = z
  .array(ApExceptionRuleSchema)
  .max(100)
  .refine(
    (rules) =>
      new Set(rules.map((rule) => rule.exceptionTypeOptionId)).size ===
      rules.length,
    { message: "Each exception type may appear in at most one rule" },
  )
  .refine(
    (rules) => {
      const keys = rules.flatMap((rule) =>
        rule.taskTypeKey ? [rule.taskTypeKey] : [],
      );
      return new Set(keys).size === keys.length;
    },
    // One open task per (type, invoice): two rules sharing a type would
    // share a task, and resolving one exception would close the other's.
    { message: "Each task type may appear in at most one rule" },
  );

export const AP_INVOICE_EXCEPTION_STATUSES = ["open", "resolved"] as const;
export type ApInvoiceExceptionStatus =
  (typeof AP_INVOICE_EXCEPTION_STATUSES)[number];

/**
 * Pick the rule that owns an invoice given the exception types currently
 * open on it: the first rule in priority order whose type is in the open set.
 * Pure — shared by the backend router and the UI preview.
 */
export function resolveOwningExceptionRule<
  R extends Pick<ApExceptionRule, "exceptionTypeOptionId">,
>(rules: readonly R[], openOptionIds: ReadonlySet<string>): R | null {
  return (
    rules.find((rule) => openOptionIds.has(rule.exceptionTypeOptionId)) ?? null
  );
}
